/**
 * VoicePlayButton — Stage 10 Voice Output.
 *
 * Speaker icon rendered below each assistant reply. First tap reads the reply
 * aloud (sentence-by-sentence so the user hears the first sentence within
 * ~150ms). Tap-again surfaces a tiny inline control rail with pause/resume,
 * stop, and a 1×/1.25× speed toggle.
 *
 * Auto-read: this component honours the global "auto-read" setting (see
 * VoiceSettingsCard). When the prop `autoPlay` flips to true on a new
 * message, playback fires automatically once the parent reply is complete.
 *
 * Tracking: `voice_played` event fires once per playback (helpful for
 * measuring whether voice mode actually retains users).
 */
import React, { useEffect, useRef, useState } from 'react';
import { SpeakerHigh, SpeakerSlash, Pause, Play, ArrowsClockwise } from '@phosphor-icons/react';
import { useTextToSpeech } from '../../hooks/useTextToSpeech';
import { track as trackEvent } from '../../services/activation';

export const VoicePlayButton = ({
  text,
  autoPlay = false,
  messageId = null,
}) => {
  const tts = useTextToSpeech();
  const [showControls, setShowControls] = useState(false);
  const playedOnceRef = useRef(false);

  // Auto-read once per message when toggle is on.
  useEffect(() => {
    if (!autoPlay || !text || playedOnceRef.current) return;
    if (!tts.supported) return;
    playedOnceRef.current = true;
    tts.play(text);
    trackEvent('voice_played', { kind: 'auto', message_id: messageId });
    setShowControls(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, text]);

  const handleClick = () => {
    if (!tts.supported) return;
    if (tts.speaking) {
      // Already playing this — toggle pause/resume on subsequent taps.
      if (tts.paused) tts.resume();
      else tts.pause();
      return;
    }
    tts.play(text);
    trackEvent('voice_played', { kind: 'manual', message_id: messageId });
    setShowControls(true);
  };

  const handleStop = () => { tts.stop(); setShowControls(false); };
  const handleReplay = () => { tts.play(text); trackEvent('voice_played', { kind: 'replay', message_id: messageId }); };
  const cycleRate = () => {
    const next = tts.rate >= 1.25 ? 1.0 : 1.25;
    tts.setRate(next);
    trackEvent('voice_rate_changed', { rate: next });
  };

  if (!tts.supported) return null;

  return (
    <div className="inline-flex items-center gap-1.5 mt-2" data-testid="voice-play-row">
      <button
        type="button"
        onClick={handleClick}
        className={[
          'inline-flex items-center justify-center rounded-full transition-colors',
          'min-w-[28px] min-h-[28px] w-7 h-7 flex-shrink-0',
          tts.speaking && !tts.paused
            ? 'bg-[#2FB3A0] text-white'
            : 'text-slate-400 hover:text-[#0E1C2E] hover:bg-slate-50 border border-transparent hover:border-[#ECE8E1]',
        ].join(' ')}
        data-testid="voice-play-button"
        aria-label={tts.speaking ? (tts.paused ? 'Resume reading' : 'Pause reading') : 'Read this aloud'}
        title={tts.speaking ? (tts.paused ? 'Resume' : 'Pause') : 'Read this aloud'}
      >
        {tts.speaking && !tts.paused
          ? <Pause size={12} weight="fill" />
          : <SpeakerHigh size={13} weight="duotone" />}
      </button>

      {showControls && (tts.speaking || tts.paused) && (
        <>
          <button
            type="button"
            onClick={handleStop}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-[#0E1C2E] hover:bg-slate-50"
            aria-label="Stop reading"
            data-testid="voice-stop-button"
          >
            <SpeakerSlash size={12} weight="duotone" />
          </button>
          <button
            type="button"
            onClick={handleReplay}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-[#0E1C2E] hover:bg-slate-50"
            aria-label="Replay from start"
            data-testid="voice-replay-button"
          >
            <ArrowsClockwise size={11} weight="duotone" />
          </button>
          <button
            type="button"
            onClick={cycleRate}
            className="inline-flex items-center justify-center px-2 h-7 rounded-full text-[10.5px] font-semibold text-slate-500 hover:text-[#0E1C2E] hover:bg-slate-50 tabular-nums"
            aria-label="Toggle speed"
            data-testid="voice-speed-button"
          >
            {tts.rate.toFixed(2).replace(/\.?0+$/, '') || '1'}×
          </button>
        </>
      )}
    </div>
  );
};

export default VoicePlayButton;
